import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Per-grid-instance state for the SSRM "Select all matching" banner UX.
 *
 * **Not** `providedIn: 'root'`. Provided in `CdwSsrmGridComponent.providers`
 * so each grid instance gets its own service — multiple SSRM grids on one
 * page can have independent prompt state.
 *
 * State machine:
 *   - `showPagePrompt$ === true` → banner shows the page-level prompt
 *     ("All N selected. [Select all M]"). Set true by
 *     `SsrmHeaderCheckboxComponent` after a header-checkbox click selects
 *     the whole page AND `itemTotal > visiblePageRows`. Set false on
 *     dismiss, on selection clear, on filter change, or when escalation
 *     completes (selection equals total → banner switches to
 *     "all selected" state automatically via derived observables).
 *   - `selectAllInProgress$ === true` → spinner on the "Select all M" link.
 *     Set true at the start of `CdwSsrmGridComponent.selectAllMatching()`,
 *     set false in its `finalize()`.
 */
@Injectable()
export class SsrmSelectAllPromptService {
  private readonly _showPagePrompt$ = new BehaviorSubject<boolean>(false);
  private readonly _selectAllInProgress$ = new BehaviorSubject<boolean>(false);

  /**
   * Emits true when the page-level "Select all matching" prompt should be
   * visible. Owned by `SsrmHeaderCheckboxComponent` (sets true) and the
   * grid component (sets false on filter change / clear).
   */
  public readonly showPagePrompt$: Observable<boolean> =
    this._showPagePrompt$.asObservable();

  /**
   * Emits true while `fetchAllIds` is in flight. Owned by the grid
   * component's `selectAllMatching()` method via rxjs `finalize()`.
   */
  public readonly selectAllInProgress$: Observable<boolean> =
    this._selectAllInProgress$.asObservable();

  public triggerPagePrompt(): void {
    this._showPagePrompt$.next(true);
  }

  public dismissPrompt(): void {
    this._showPagePrompt$.next(false);
  }

  public setSelectAllInProgress(busy: boolean): void {
    this._selectAllInProgress$.next(busy);
  }

  /** Reset both flags. Called on filter change and on clearSelection. */
  public clear(): void {
    this.dismissPrompt();
    this.setSelectAllInProgress(false);
  }
}
